import { EyeOff, Gift, Heart } from 'lucide-react'
import { motion } from 'motion/react'
import { useImageBackground } from '@/hooks/useImageBackground'
import { getCategoryColor, getCategoryInlineStyle } from '@/lib/categoryColors'
import { useLanguage } from '@/context/LanguageContext'
import { translateCategory } from '@/lib/categoryColors'

interface DashboardItemImageProps {
  imageUrl: string | null
  title: string
  isReserved: boolean
  isHidden?: boolean
  category?: string
  color?: string | null
  bgColor?: string | null
  isOwner?: boolean
}

export default function DashboardItemImage({
  imageUrl,
  title,
  isReserved,
  isHidden = false,
  category,
  bgColor,
  isOwner = false,
}: DashboardItemImageProps) {
  const { language } = useLanguage()
  const autoBg = useImageBackground(imageUrl)
  const bg = bgColor || autoBg
  const badgeClass = category ? getCategoryColor(category) : null

  return (
    <div className="relative aspect-square overflow-hidden rounded-t-lg" style={{ backgroundColor: bg }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={title}
          className={`h-full w-full object-contain ${isReserved || isHidden ? 'opacity-50' : ''}`}
          onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0' }}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-100 to-violet-100">
          <Gift className="h-12 w-12 text-indigo-300" />
        </div>
      )}
      {isReserved && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.08 }}
          transition={{ type: 'spring', stiffness: 400, damping: 15 }}
          className="absolute inset-0 z-10 flex items-center justify-center"
        >
          <div
            className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs md:text-sm font-extrabold text-white shadow-lg"
            style={{ backgroundColor: '#DE256D' }}
          >
            <Heart className="w-3.5 h-3.5 fill-current" />
            {language === 'en' ? 'Reserved' : 'Prenotato'}
          </div>
        </motion.div>
      )}
      {isHidden && !isReserved && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.08 }}
          transition={{ type: 'spring', stiffness: 400, damping: 15 }}
          className="absolute inset-0 z-10 flex items-center justify-center"
        >
          <div className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs md:text-sm font-extrabold text-white shadow-lg bg-sky-500">
            <EyeOff className="w-3.5 h-3.5" />
            {language === 'en' ? 'Hidden' : 'Nascosto'}
          </div>
        </motion.div>
      )}
      {badgeClass && category && (
        <span className={`absolute top-3 left-3 inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold text-[10px] md:text-xs shadow-sm z-10 ${badgeClass}`} style={getCategoryInlineStyle(category)}>
          {translateCategory(category, language)}
        </span>
      )}
    </div>
  )
}
